import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Paper,
  CircularProgress,
  Checkbox,
  ListItemSecondaryAction,
} from "@mui/material"
import { Editor, EditorState, convertFromRaw } from "draft-js"
import "draft-js/dist/Draft.css"
import CircleIcon from "@mui/icons-material/Circle"

const RecipeDetail = () => {
  const { id } = useParams()
  const [recipe, setRecipe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  )

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const response = await fetch(`/api/recipes/byIDs?ids=${id}`)

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }

        const [recipeData] = await response.json() // The API returns an array

        if (recipeData) {
          setRecipe(recipeData)

          if (recipeData.instructions && recipeData.instructions.length > 0) {
            const rawContent = {
              blocks: recipeData.instructions[0].blocks,
              entityMap: recipeData.instructions[0].entityMap || {},
            }
            const contentState = convertFromRaw(rawContent)
            setEditorState(EditorState.createWithContent(contentState))
          }
        }
      } catch (error) {
        console.error("Error fetching recipe:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchRecipe()
  }, [id])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (!recipe) {
    return (
      <Typography variant="h4" align="center" mt={5}>
        Recipe not found!
      </Typography>
    )
  }

  const imageBlockRenderer = (contentBlock) => {
    if (contentBlock.getType() === "atomic") {
      return {
        component: ImageComponent,
        editable: false,
      }
    }
    return null
  }

  const ImageComponent = (props) => {
    const entity = props.contentState.getEntity(props.block.getEntityAt(0))
    const { src } = entity.getData()
    return (
      <img src={src} alt="" style={{ maxWidth: "100%", margin: "10px 0" }} />
    )
  }

  return (
    <Box mt={4} mx={2}>
      <Grid container spacing={4}>
        {/* Left Section: Recipe Image */}
        <Grid item xs={12} md={12} lg={7}>
          <Card elevation={4}>
            <CardMedia
              component="img"
              alt={recipe.title}
              height="400"
              image={recipe.thumbnail || "/default-recipe.jpg"}
              title={recipe.title}
            />
          </Card>
        </Grid>

        {/* Right Section: Recipe Details */}
        <Grid item xs={12} md={12} lg={5}>
          <Typography variant="h3" gutterBottom>
            {recipe.title}
          </Typography>

          {/* Ingredients Card */}
          <Card elevation={3} sx={{ mb: 4 }}>
            <CardContent>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Typography variant="h5" gutterBottom>
                    Ingredients
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    style={{ paddingRight: "17px" }}
                  >
                    Checklist
                  </Typography>
                </Grid>
              </Grid>
              <List>
                {recipe.ingredients.map((ingredient, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemIcon sx={{ minWidth: "20px" }}>
                      <CircleIcon sx={{ fontSize: 12 }} />
                    </ListItemIcon>
                    <ListItemText primary={ingredient} sx={{ marginLeft: 1 }} />
                    <ListItemSecondaryAction>
                      <Checkbox
                        disableRipple
                        disableFocusRipple
                        edge="end"
                        sx={{ paddingRight: "50px", paddingLeft: "50px" }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions Section: Full Width */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Instructions
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  padding: "15px",
                  minHeight: "200px",
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                }}
              >
                <Editor
                  editorState={editorState}
                  readOnly={true}
                  blockRendererFn={imageBlockRenderer}
                />
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default RecipeDetail
